import React, { useMemo } from "react";
import * as THREE from "three";
import { Line, Text } from "@react-three/drei";
import type { SubsystemInstance, SubsystemType } from "../../types/subsystems";
import { useThermalDesign, useSubsystemConfig } from "../../../../ThermalDesignContext";
import {
  FACE_TO_DECK_MAP,
  SUBSYSTEM_COLORS,
  SUBSYSTEM_OPACITY,
  STRUCTURAL_COLORS,
  STRUCTURAL_OPACITY,
  EDGE_ANGLE_THRESHOLD,
  convertDimensionsToMeters,
  calculateSubsystemPosition,
  convertRotationToRadians,
} from "../../types/subsystems";
import { useFaceIsSelected, useSelectedFace } from "./useSelectedFace";

const DECK_Y = 5 / 6;

interface SubsystemLabelProps {
  label: string;
  height: number;
}

const SubsystemLabel: React.FC<SubsystemLabelProps> = ({ label, height }) => {
  const labelYOffset = height / 2 + 0.1;

  return (
    <Text
      position={[0, labelYOffset, 0]}
      fontSize={0.08}
      color="white"
      anchorX="center"
      anchorY="bottom"
    >
      {label}
    </Text>
  );
};

interface GeometryRendererProps {
  dimensions: SubsystemInstance["dimensions"];
  visual: SubsystemInstance["visual"];
  isSelected?: boolean;
}

const GeometryRenderer: React.FC<GeometryRendererProps> = ({
  dimensions,
  visual,
  isSelected = false,
}) => {
  const { width, height, length } = convertDimensionsToMeters(dimensions);
  const color = isSelected
    ? SUBSYSTEM_COLORS.SELECTED
    : (visual.inactiveColor || visual.color);
  const opacity = isSelected
    ? SUBSYSTEM_OPACITY.SELECTED
    : (visual.inactiveOpacity ?? visual.opacity);
  const edgeColor = isSelected
    ? SUBSYSTEM_COLORS.SELECTED_EDGE
    : (visual.inactiveColor || visual.color);
  const edgeOpacity = isSelected
    ? SUBSYSTEM_OPACITY.SELECTED_EDGE
    : SUBSYSTEM_OPACITY.INACTIVE_EDGE;

  const meshGeometry = useMemo(() => {
    switch (visual.geometry) {
      case "box":
        return <boxGeometry args={[width, height, length]} />;
      case "cylinder":
        return <cylinderGeometry args={[width / 2, width / 2, height, 32]} />;
      case "sphere":
        return <sphereGeometry args={[width / 2, 32, 32]} />;
      default:
        return <boxGeometry args={[width, height, length]} />;
    }
  }, [width, height, length, visual.geometry]);

  const edgeGeometry = useMemo(() => {
    if (visual.geometry === "box") {
      return new THREE.EdgesGeometry(
        new THREE.BoxGeometry(width, height, length),
        EDGE_ANGLE_THRESHOLD
      );
    } else if (visual.geometry === "cylinder") {
      return new THREE.EdgesGeometry(
        new THREE.CylinderGeometry(width / 2, width / 2, height, 32),
        EDGE_ANGLE_THRESHOLD
      );
    } else if (visual.geometry === "sphere") {
      return new THREE.EdgesGeometry(
        new THREE.SphereGeometry(width / 2, 32, 32),
        EDGE_ANGLE_THRESHOLD
      );
    }
    return new THREE.EdgesGeometry(
      new THREE.BoxGeometry(width, height, length),
      EDGE_ANGLE_THRESHOLD
    );
  }, [width, height, length, visual.geometry]);

  return (
    <>
      <mesh>
        {meshGeometry}
        <meshStandardMaterial
          color={color}
          opacity={opacity}
          transparent={opacity < 1}
        />
      </mesh>
      {isSelected && (
        <lineSegments geometry={edgeGeometry}>
          <lineBasicMaterial
            color={edgeColor}
            linewidth={2}
            opacity={edgeOpacity}
            transparent
          />
        </lineSegments>
      )}
    </>
  );
};

interface SubsystemProps {
  dimensions: SubsystemInstance["dimensions"];
  visual: SubsystemInstance["visual"];
  isSelected?: boolean;
}

const createSubsystemComponent = (name: string) => {
  const Component: React.FC<SubsystemProps> = ({
    dimensions,
    visual,
    isSelected = true,
  }) => {
    return (
      <group name={name}>
        <GeometryRenderer
          dimensions={dimensions}
          visual={visual}
          isSelected={isSelected}
        />
      </group>
    );
  };
  Component.displayName = name;
  return Component;
};

export const XBandModule = createSubsystemComponent("x-band-module");
export const SBandModule = createSubsystemComponent("s-band-module");
export const ReactionWheelModule = createSubsystemComponent("reaction-wheel-module");
export const StarTrackerModule = createSubsystemComponent("star-tracker-module");
export const PropulsionTank = createSubsystemComponent("propulsion-tank");
export const Thruster = createSubsystemComponent("thruster");
export const AvionicsModule = createSubsystemComponent("avionics-module");
export const PowerSystemModule = createSubsystemComponent("power-system-module");
export const BatteryPack = createSubsystemComponent("battery-pack");
export const FlexCoreAvionics = createSubsystemComponent("flex-core-avionics");
export const AISModule = createSubsystemComponent("ais-module");
export const TCU = createSubsystemComponent("tcu");
export const PMS = createSubsystemComponent("pms");
export const IDRSSSDR = createSubsystemComponent("idrss-sdr");

export const TopCylinder: React.FC = () => {
  const { showSubsystemView } = useThermalDesign();
  const isSelected = useFaceIsSelected("-X");

  const outerRadius = 5 / 3;
  const height = 5 / 10;
  const thickness = 5 / 10;
  const innerRadius = outerRadius - thickness;

  const geometry = useMemo(() => {
    const points = [
      new THREE.Vector2(innerRadius, 0),
      new THREE.Vector2(outerRadius, 0),
      new THREE.Vector2(outerRadius, height),
      new THREE.Vector2(innerRadius, height),
      new THREE.Vector2(innerRadius, 0),
    ];
    return new THREE.LatheGeometry(points, 64);
  }, [innerRadius, outerRadius, height]);

  const edgeGeometry = useMemo(
    () => new THREE.EdgesGeometry(geometry, EDGE_ANGLE_THRESHOLD),
    [geometry]
  );

  let fillColor: string = isSelected ? STRUCTURAL_COLORS.ACTIVE : STRUCTURAL_COLORS.INACTIVE;
  let fillOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_FILL : STRUCTURAL_OPACITY.INACTIVE_FILL;
  let edgeOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_EDGE : STRUCTURAL_OPACITY.INACTIVE_EDGE;
  let edgeColor: string = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE_EDGE;

  if (showSubsystemView && isSelected) {
    fillColor = SUBSYSTEM_COLORS.SELECTED;
    fillOpacity = SUBSYSTEM_OPACITY.SELECTED;
    edgeColor = SUBSYSTEM_COLORS.SELECTED_EDGE;
    edgeOpacity = SUBSYSTEM_OPACITY.SELECTED_EDGE;
  } else if (showSubsystemView) {
    fillOpacity = SUBSYSTEM_OPACITY.UNSELECTED_FACE;
    edgeOpacity = SUBSYSTEM_OPACITY.UNSELECTED_FACE;
  }

  return (
    <group position={[0, 2.501, 0]}>
      <mesh geometry={geometry}>
        <meshBasicMaterial
          color={fillColor}
          transparent
          opacity={fillOpacity}
          side={THREE.DoubleSide}
        />
      </mesh>
      <lineSegments geometry={edgeGeometry}>
        <lineBasicMaterial color={edgeColor} transparent opacity={edgeOpacity} />
      </lineSegments>
    </group>
  );
};

export const BottomCylinder: React.FC = () => {
  const { showSubsystemView } = useThermalDesign();
  const isSelected = useFaceIsSelected("+X");

  const radius = 4 / 3;
  const bottomY = -2.501;
  const topY = -DECK_Y;
  const height = topY - bottomY;
  const centerY = (topY + bottomY) / 2;

  const geometry = useMemo(
    () => new THREE.CylinderGeometry(radius, radius, height, 64, 1, true),
    [radius, height]
  );
  const edgeGeometry = useMemo(
    () => new THREE.EdgesGeometry(geometry, EDGE_ANGLE_THRESHOLD),
    [geometry]
  );

  let fillColor: string = isSelected ? STRUCTURAL_COLORS.ACTIVE : STRUCTURAL_COLORS.INACTIVE;
  let fillOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_FILL : STRUCTURAL_OPACITY.INACTIVE_FILL_ALT;
  let edgeOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_EDGE : STRUCTURAL_OPACITY.INACTIVE_EDGE_ALT;
  let edgeColor: string = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE;

  if (showSubsystemView && isSelected) {
    fillColor = SUBSYSTEM_COLORS.SELECTED;
    fillOpacity = SUBSYSTEM_OPACITY.SELECTED;
    edgeColor = SUBSYSTEM_COLORS.SELECTED_EDGE;
    edgeOpacity = SUBSYSTEM_OPACITY.SELECTED_EDGE;
  } else if (showSubsystemView) {
    fillOpacity = SUBSYSTEM_OPACITY.UNSELECTED_FACE;
    edgeOpacity = SUBSYSTEM_OPACITY.UNSELECTED_FACE;
  }

  return (
    <group position={[0, centerY, 0]}>
      <mesh geometry={geometry}>
        <meshBasicMaterial
          color={fillColor}
          transparent
          opacity={fillOpacity}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
      <lineSegments geometry={edgeGeometry}>
        <lineBasicMaterial color={edgeColor} transparent opacity={edgeOpacity} />
      </lineSegments>
    </group>
  );
};

export const Telescope: React.FC = () => {
  const { showSubsystemView } = useThermalDesign();
  const isSelected = useFaceIsSelected("+X");

  const strutData = useMemo(() => {
    const bigR = 4 / 3;
    const smallR = 5 / 15;
    const y = -2.501;
    const strutR = 0.04;
    const length = bigR - smallR;

    // 4 struts at 0°, 90°, 180°, 270° forming a "+" in the bottom-face plane
    return [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2].map((angle) => {
      const midR = (bigR + smallR) / 2;
      const mid = new THREE.Vector3(
        midR * Math.cos(angle),
        y,
        midR * Math.sin(angle)
      );
      // direction from big rim inward toward aperture center
      const dir = new THREE.Vector3(-Math.cos(angle), 0, -Math.sin(angle));
      const quat = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        dir
      );
      const euler = new THREE.Euler().setFromQuaternion(quat);
      return {
        mid: [mid.x, mid.y, mid.z] as [number, number, number],
        rotation: [euler.x, euler.y, euler.z] as [number, number, number],
        length,
        radius: strutR,
      };
    });
  }, []);

  const apertureRadius = 5 / 15;
  const apertureY = -2.501;

  let fillColor: string = isSelected ? STRUCTURAL_COLORS.ACTIVE : STRUCTURAL_COLORS.INACTIVE;
  let fillOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_FILL : STRUCTURAL_OPACITY.INACTIVE_FILL;
  let edgeOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_EDGE : STRUCTURAL_OPACITY.INACTIVE_EDGE;
  let edgeColor: string = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE;

  if (showSubsystemView && isSelected) {
    fillColor = SUBSYSTEM_COLORS.SELECTED;
    fillOpacity = SUBSYSTEM_OPACITY.SELECTED;
    edgeColor = SUBSYSTEM_COLORS.SELECTED_EDGE;
    edgeOpacity = SUBSYSTEM_OPACITY.SELECTED_EDGE;
  } else if (showSubsystemView) {
    fillOpacity = SUBSYSTEM_OPACITY.UNSELECTED_FACE;
    edgeOpacity = SUBSYSTEM_OPACITY.UNSELECTED_FACE;
  }

  const circlePoints = useMemo(() => {
    const pts: THREE.Vector3[] = [];
    const segments = 64;
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * 2 * Math.PI;
      pts.push(
        new THREE.Vector3(
          apertureRadius * Math.cos(t),
          apertureY,
          apertureRadius * Math.sin(t)
        )
      );
    }
    return pts;
  }, [apertureRadius, apertureY]);

  return (
    <group>
      {strutData.map((s, i) => (
        <group key={i} position={s.mid} rotation={s.rotation}>
          <mesh>
            <cylinderGeometry args={[s.radius, s.radius, s.length, 16]} />
            <meshBasicMaterial
              color={fillColor}
              transparent
              opacity={fillOpacity}
            />
          </mesh>
        </group>
      ))}
      <Line
        points={circlePoints}
        color={edgeColor}
        lineWidth={2}
        transparent
        opacity={edgeOpacity}
      />
    </group>
  );
};

const SUBSYSTEM_COMPONENT_MAP: Record<
  SubsystemType,
  React.ComponentType<React.ComponentProps<typeof XBandModule>>
> = {
  "x-band": XBandModule,
  "s-band": SBandModule,
  "reaction-wheel": ReactionWheelModule,
  "star-tracker": StarTrackerModule,
  "propulsion-tank": PropulsionTank,
  "thruster": Thruster,
  "avionics": AvionicsModule,
  "power-system": PowerSystemModule,
  "battery-pack": BatteryPack,
  "flex-core-avionics": FlexCoreAvionics,
  "ais-module": AISModule,
  "tcu": TCU,
  "pms": PMS,
  "idrss-sdr": IDRSSSDR,
};

interface SubsystemRendererProps {}

const SubsystemRenderer: React.FC<SubsystemRendererProps> = () => {
  const { subsystemInstances, showSubsystems, showSubsystemView, showSubsystemLabels } = useThermalDesign();
  const { selectedSubsystemId } = useSubsystemConfig();
  const selectedFace = useSelectedFace();

  if (!showSubsystems || !showSubsystemView || !selectedFace) {
    return null;
  }

  const selectedDeck = FACE_TO_DECK_MAP[selectedFace];

  const renderSubsystem = (instance: SubsystemInstance) => {
    if (!instance.enabled) {
      return null;
    }

    const SubsystemComponent = SUBSYSTEM_COMPONENT_MAP[instance.type];
    if (!SubsystemComponent) {
      return null;
    }

    const position = calculateSubsystemPosition(
      instance.mountLocation.nodeId,
      instance.mountLocation.deck,
      instance.dimensions,
      instance.mountLocation.positionOffset
    );
    const rotation = convertRotationToRadians(instance.rotation);
    const isSelected = selectedSubsystemId === instance.id;
    const { height } = convertDimensionsToMeters(instance.dimensions);

    return (
      <group key={instance.id} position={position} rotation={rotation}>
        <SubsystemComponent
          dimensions={instance.dimensions}
          visual={instance.visual}
          isSelected={isSelected}
        />
        {showSubsystemLabels && <SubsystemLabel label={instance.label} height={height} />}
      </group>
    );
  };

  const visibleSubsystems = subsystemInstances.filter(
    (instance) => instance.enabled && instance.mountLocation.deck === selectedDeck
  );

  return <group name="subsystems">{visibleSubsystems.map(renderSubsystem)}</group>;
};

export default SubsystemRenderer;
